import type {
  JourneyDefinition,
  PlanAction,
  PlanActionScreen,
  PlanStep,
  Substep,
} from "./plan.types";
import { isFocusableSection } from "./resolveCycleDetailAction";

const SUPPORTED_ACTION_SCREENS: ReadonlySet<PlanActionScreen> = new Set([
  "AllPastCycles",
  "ArticleDetail",
  "AtHomeFertilityTesting",
  "CareOverview",
  "CostsOverviewScreen",
  "CoverageFlow",
  "CryoVaultScreen",
  "CycleDetail",
  "CycleDetailScreen",
  "DocumentsScreen",
  "FindCareScreen",
  "LearnScreen",
  "MenstrualHealthFlow",
  "MedicalHistory",
  "PharmacyScreen",
  "ProgramsScreen",
  "SupplementsScreen",
]);

function isSupportedActionScreen(screen: string): screen is PlanActionScreen {
  return SUPPORTED_ACTION_SCREENS.has(screen as PlanActionScreen);
}

function validateAction(action: PlanAction, path: string): string[] {
  const hasDestination = Boolean(action.screen || action.url);
  const hasSignal = Boolean(action.signal);
  const errors: string[] = [];

  if (hasDestination === hasSignal) {
    errors.push(
      `${path} ("${action.label}") must have exactly one destination or signal`,
    );
  }

  if (action.screen && !isSupportedActionScreen(action.screen)) {
    errors.push(`${path} uses unsupported screen "${action.screen}"`);
  }

  if (action.signal === "complete-step" && !action.params?.stepId) {
    errors.push(`${path} must provide the stepId it completes`);
  }

  if (action.screen === "ArticleDetail" && !action.params?.articleId) {
    errors.push(`${path} must provide a locale-safe articleId`);
  }

  if (action.screen === "SupplementsScreen" && action.params?.filter) {
    errors.push(
      `${path} uses an unsupported filter parameter for SupplementsScreen; use goal instead`,
    );
  }

  if (action.screen === "LearnScreen" && action.params?.article) {
    errors.push(
      `${path} uses LearnScreen with an unsupported article parameter; use ArticleDetail instead`,
    );
  }

  // An id-less cycle-detail action is legitimate when it names a section: the
  // concept builders are static and hold no cycle id, so `focus` is how an action
  // says "this patient's open cycle" and the screen supplies the id it knows.
  // Rejecting those outright would throw the moment one is placed in a journey
  // step — including the monitoring and beta actions Home already ships.
  if (
    (action.screen === "CycleDetail" ||
      action.screen === "CycleDetailScreen") &&
    !action.params?.cycleId &&
    !isFocusableSection(action.params?.focus)
  ) {
    errors.push(
      `${path} must provide cycleId, focus a section, or use a start-treatment signal`,
    );
  }

  return errors;
}

function validateSubstep(substep: Substep, path: string): string[] {
  const errors = (substep.actions ?? []).flatMap((action, index) =>
    validateAction(action, `${path}.actions[${index}]`),
  );

  for (const [index, item] of (substep.inTheMeantime ?? []).entries()) {
    if (item.action) {
      errors.push(
        ...validateAction(
          item.action,
          `${path}.inTheMeantime[${index}].action`,
        ),
      );
    }
  }

  return errors;
}

function validateStep(step: PlanStep, path: string): string[] {
  const errors = (step.actions ?? []).flatMap((action, index) =>
    validateAction(action, `${path}.actions[${index}]`),
  );

  for (const [index, substep] of (step.substeps ?? []).entries()) {
    errors.push(...validateSubstep(substep, `${path}.substeps[${index}]`));
  }

  for (const [index, substep] of (
    step.partnerTrack?.substeps ?? []
  ).entries()) {
    errors.push(
      ...validateSubstep(substep, `${path}.partnerTrack.substeps[${index}]`),
    );
  }

  for (const [index, modifier] of (step.conditionModifiers ?? []).entries()) {
    for (const [actionIndex, action] of (
      modifier.modifiedActions ?? []
    ).entries()) {
      errors.push(
        ...validateAction(
          action,
          `${path}.conditionModifiers[${index}].modifiedActions[${actionIndex}]`,
        ),
      );
    }
  }

  const optimization = step.optimizationWindow;
  if (optimization) {
    for (const [index, item] of [
      ...optimization.lifestyle,
      ...optimization.supplements,
    ].entries()) {
      if (item.action) {
        errors.push(
          ...validateAction(
            item.action,
            `${path}.optimization[${index}].action`,
          ),
        );
      }
    }
    if (optimization.program?.action) {
      errors.push(
        ...validateAction(
          optimization.program.action,
          `${path}.optimization.program.action`,
        ),
      );
    }
  }

  return errors;
}

export function validateJourneyActions(journey: JourneyDefinition): string[] {
  return journey.steps.flatMap((step, index) =>
    validateStep(step, `${journey.key}.steps[${index}]`),
  );
}

export function assertValidJourneyActions(journey: JourneyDefinition): void {
  const errors = validateJourneyActions(journey);
  if (errors.length > 0) {
    throw new Error(`Invalid homepage actions:\n${errors.join("\n")}`);
  }
}
